import React, { useState } from "react";
import { View, StyleSheet, ScrollView, ActivityIndicator } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import DiscussionFormModal from "../../../components/adminDashboard/DiscussionFormModal/DiscussionFormModal";
import AdminDiscussionCard from "../../../components/adminDashboard/AdminDiscussionCard/AdminDiscussionCard";
import DeleteResource from "../../../components/content/DeleteResource/DeleteResource";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import DiscussionPanel from "../../../components/adminDashboard/DiscussionPanel/DiscussionPanel";
import {
  useGetThreadsByCourseQuery,
  useGetStudentsByCourseIdQuery,
  useDeleteThreadMutation,
} from "@axio/graphql/generated/graphql";
import { ChatsCircle } from "@phosphor-icons/react/dist/ssr/ChatsCircle";
import { Users } from "@phosphor-icons/react/dist/ssr/Users";
import { ChatCircleText } from "@phosphor-icons/react/dist/ssr/ChatCircleText";
import { TrendUp } from "@phosphor-icons/react/dist/ssr/TrendUp";
import { Plus } from "@phosphor-icons/react/dist/ssr/Plus";

interface AdminCommunityContainerProps {
  courseId: string;
}

const AdminCommunityContainer: React.FC<AdminCommunityContainerProps> = ({ courseId }) => {
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [editingThread, setEditingThread] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingThread, setDeletingThread] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDiscussionPanel, setShowDiscussionPanel] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);

  const [deleteThreadMutation] = useDeleteThreadMutation();

  const {
    data: threadsData,
    loading,
    error,
    refetch,
  } = useGetThreadsByCourseQuery({
    variables: {
      courseId,
    },
  });

  const { data: studentsData, loading: studentsLoading } = useGetStudentsByCourseIdQuery({
    variables: {
      courseId,
      searchQuery: "",
      page: 1,
      pageSize: 1000,
      sortBy: "name_asc",
    },
  });

  const threads = threadsData?.getThreadsByCourse?.data || [];
  const students = studentsData?.getStudentsByCourseId?.data || [];

  const sortedThreads = [...threads].sort((a, b) => {
    return new Date(b.created).getTime() - new Date(a.created).getTime();
  });

  // Calculate real stats from data
  const calculateStats = () => {
    const totalStudents = students.length;
    const totalPosts = threads.reduce((total, thread) => total + (thread.posts?.length || 0), 0);

    // Create a set of student IDs for quick lookup
    const studentIds = new Set(students.map((student) => student.id));

    // Calculate unique STUDENT participants across all threads (exclude faculty)
    const studentParticipantIds = new Set<string>();
    threads.forEach((thread) => {
      thread.posts?.forEach((post) => {
        if (post.author?.id && studentIds.has(post.author.id)) {
          studentParticipantIds.add(post.author.id);
        }
      });
    });

    const uniqueStudentParticipants = studentParticipantIds.size;

    // Calculate engagement rate as: (unique student-thread participations) / (total possible student-thread participations)
    // For each thread, count unique students who participated (not total posts)
    const totalThreads = threads.length;
    const maxPossibleParticipations = totalStudents * totalThreads;

    // Count unique student participations per thread
    let actualStudentParticipations = 0;
    threads.forEach((thread) => {
      const studentsInThisThread = new Set<string>();
      thread.posts?.forEach((post) => {
        if (post.author?.id && studentIds.has(post.author.id)) {
          studentsInThisThread.add(post.author.id);
        }
      });
      actualStudentParticipations += studentsInThisThread.size;
    });

    const engagementRate =
      maxPossibleParticipations > 0 ? Math.round((actualStudentParticipations / maxPossibleParticipations) * 100) : 0;

    return {
      activeDiscussions: threads.length,
      participants: uniqueStudentParticipants,
      totalPosts,
      engagementRate,
    };
  };

  const stats = calculateStats();

  const handleCreateDiscussion = () => {
    setEditingThread(null);
    setShowDiscussionModal(true);
  };

  const handleEditThread = (thread: any) => {
    setEditingThread(thread);
    setShowDiscussionModal(true);
  };

  const handleViewThread = (thread: any) => {
    // Add courseId to the thread object when passing to DiscussionPanel
    const threadWithCourseId = {
      ...thread,
      courseId: courseId,
    };
    setSelectedThread(threadWithCourseId);
    setShowDiscussionPanel(true);
  };

  const handleCloseDiscussionPanel = () => {
    setShowDiscussionPanel(false);
    setSelectedThread(null);
    refetch();
  };

  const handleDeleteThread = (threadId: string, title: string) => {
    setDeletingThread({ id: threadId, title });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingThread) return;

    setIsDeleting(true);

    try {
      const result = await deleteThreadMutation({
        variables: {
          threadId: deletingThread.id,
        },
      });

      if (!result.data?.deleteThread?.success) {
        throw new Error(result.data?.deleteThread?.message || "Failed to delete thread");
      }

      setShowDeleteModal(false);
      setDeletingThread(null);
      await refetch();
    } catch (error) {
      // TODO: Show error toast notification to user
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingThread(null);
  };

  const handleModalClose = () => {
    setShowDiscussionModal(false);
    setEditingThread(null);
    refetch();
  };

  if (loading || studentsLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#3E68FE" />
          <CustomSpacing type="vertical" size="m" />
          <CustomText text="Loading community data..." size="m" style={styles.loadingText} useTranslationText={false} />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <CustomText
            text="Error loading community data"
            size="l"
            weight="bold"
            style={styles.errorTitle}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text="Please try refreshing the page."
            size="m"
            style={styles.errorDescription}
            useTranslationText={false}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <CustomText text="Course Community" size="xl" weight="bold" style={styles.title} useTranslationText={false} />
          <CustomText
            text="Manage discussion boards and community features for this course"
            size="m"
            style={styles.subtitle}
            useTranslationText={false}
          />
        </View>
        <CustomButton
          text="Create Discussion"
          styleType="primary"
          size="m"
          bold={true}
          leftIcon={<Plus size={24} color="#FFFFFF" />}
          onPress={handleCreateDiscussion}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {/* Stats Cards */}
      <View style={styles.statsContainer}>
        <View style={styles.statCard}>
          <View style={[styles.statIcon, styles.discussionsIcon]}>
            <ChatsCircle size={18} color="#FFFFFF" />
          </View>
          <View style={styles.statContent}>
            <CustomText
              text={stats.activeDiscussions.toString()}
              size="xl"
              weight="ultraBold"
              style={styles.statNumber}
              useTranslationText={false}
            />
            <CustomText
              text="Active Discussions"
              size="xs"
              weight="medium"
              style={styles.statLabel}
              useTranslationText={false}
            />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={[styles.statIcon, styles.participantsIcon]}>
            <Users size={18} color="#FFFFFF" />
          </View>
          <View style={styles.statContent}>
            <CustomText
              text={stats.participants.toString()}
              size="xl"
              weight="ultraBold"
              style={styles.statNumber}
              useTranslationText={false}
            />
            <CustomText
              text="Student Participants"
              size="xs"
              weight="medium"
              style={styles.statLabel}
              useTranslationText={false}
            />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={[styles.statIcon, styles.postsIcon]}>
            <ChatCircleText size={18} color="#FFFFFF" />
          </View>
          <View style={styles.statContent}>
            <CustomText
              text={stats.totalPosts.toString()}
              size="xl"
              weight="ultraBold"
              style={styles.statNumber}
              useTranslationText={false}
            />
            <CustomText
              text="Total Posts"
              size="xs"
              weight="medium"
              style={styles.statLabel}
              useTranslationText={false}
            />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={[styles.statIcon, styles.engagementIcon]}>
            <TrendUp size={18} color="#FFFFFF" />
          </View>
          <View style={styles.statContent}>
            <CustomText
              text={`${stats.engagementRate}%`}
              size="xl"
              weight="ultraBold"
              style={styles.statNumber}
              useTranslationText={false}
            />
            <CustomText
              text="Engagement Rate"
              size="xs"
              weight="medium"
              style={styles.statLabel}
              useTranslationText={false}
            />
          </View>
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {/* Discussions List */}
      {sortedThreads.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText
            text="No discussions yet"
            size="xl"
            weight="bold"
            style={styles.emptyTitle}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text="Create your first discussion to start engaging with students."
            size="m"
            style={styles.emptyDescription}
            useTranslationText={false}
          />
        </View>
      ) : (
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false} tabIndex={0}>
          {sortedThreads.map((thread) => (
            <View key={thread.id} style={styles.cardContainer}>
              <AdminDiscussionCard
                thread={thread}
                onEdit={handleEditThread}
                onDelete={handleDeleteThread}
                onView={handleViewThread}
              />
            </View>
          ))}
        </ScrollView>
      )}

      <DiscussionFormModal
        visible={showDiscussionModal}
        onClose={handleModalClose}
        courseId={courseId}
        thread={editingThread}
      />

      <CustomModal
        visible={showDeleteModal}
        onRequestClose={handleCancelDelete}
        ariaLabel="Delete Discussion Confirmation"
        showCloseButton={false}>
        <DeleteResource
          deleteButtonClick={handleConfirmDelete}
          backButtonClick={handleCancelDelete}
          type="discussion"
          isDeleting={isDeleting}
        />
      </CustomModal>

      <DiscussionPanel
        isOpen={showDiscussionPanel}
        onClose={handleCloseDiscussionPanel}
        selectedThread={selectedThread}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  loadingText: {
    color: "#6B7280",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 80,
  },
  errorTitle: {
    color: "#101828",
    textAlign: "center",
  },
  errorDescription: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 400,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerContent: {
    flex: 1,
  },
  title: {
    color: "#101828",
  },
  subtitle: {
    color: "#6B7280",
    marginTop: 4,
  },
  statsContainer: {
    flexDirection: "row",
    gap: 24,
  },
  statCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 18,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  discussionsIcon: {
    backgroundColor: "#3E68FE",
  },
  participantsIcon: {
    backgroundColor: "#913DAF",
  },
  postsIcon: {
    backgroundColor: "#1A8E9D",
  },
  engagementIcon: {
    backgroundColor: "#22C55E",
  },
  statContent: {
    flex: 1,
  },
  statNumber: {
    color: "#101828",
    lineHeight: 32,
  },
  statLabel: {
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 4,
  },
  emptyState: {
    flex: 1,
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    padding: 32,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderStyle: "dashed",
  },
  emptyTitle: {
    color: "#101828",
    textAlign: "center",
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
  },
  scrollView: {
    flex: 1,
  },
  cardContainer: {
    marginBottom: 16,
  },
});

export default AdminCommunityContainer;
